import type {Component} from 'panel';

import type {Store, SubscriptionCallback} from 'common/report/stores/store';

export function useEventListener(
  component: Component<any, any, any, any, any>,
  target: EventTarget,
  event: string,
  listener: EventListenerOrEventListenerObject,
  listenerOptions?: AddEventListenerOptions,
) {
  target.addEventListener(event, listener, listenerOptions);
  component.onDisconnected(() => target.removeEventListener(event, listener, listenerOptions));
}

/**
 * Provides component integration for a particular store by adding
 * a subscriber that forces an update on the component by default
 * or invokes subscriptionCallback if passed; this subscription
 * is removed when the component is unmounted
 *
 * @param component
 * @param store
 * @param subscriptionCallback
 */
export function useStore<StoreValueT, StoreValueTRaw>(
  // TODO: this should be of the type `Component<unknown>`, but app components that use JSURLMixin mangles the type, needs to be fixed
  component: TODO,
  store: Store<StoreValueT, StoreValueTRaw>,
  subscriptionCallback?: SubscriptionCallback,
): Store<StoreValueT, StoreValueTRaw> {
  if (!subscriptionCallback) {
    subscriptionCallback = component.update.bind(component);
  }
  store.subscribe(subscriptionCallback);
  component.onDisconnected(() => store.unsubscribe(subscriptionCallback));
  return store;
}

/**
 * Listen for prop changes in a component. When one occurs, call the callback.
 *
 * Uses for this:
 * - as a convenience to automatically define the getters and setters for a known prop
 * - as a way to provide a debounced callback for a group of linked props
 */
export function listenForPropUpdate<Host, Prop extends keyof Host>(host: Host, propName: Prop, callback: VoidFunction) {
  Object.defineProperty(host, propName, {
    get() {
      return host[`_${propName}`];
    },

    set(val) {
      host[`_${propName}`] = val;
      callback();
    },
  });
}
